import { useState, useCallback, useEffect } from 'react';
import { bankBalanceApi } from '../services/bankBalanceApi';
import { useToast } from '../components/ui/Toast';
import type {
    BankBalanceEntry,
    BankAccount,
    CurrencySummary,
    BankComparison,
    SyncCheckResult,
    CreateEntryData,
    UpdateEntryData,
    CopyEntriesData,
} from '../types/bankBalance';

export function useBankBalance() {
    const { toast } = useToast();

    // Filters
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [companyCode, setCompanyCode] = useState<string | null>(null);

    // Data
    const [entries, setEntries] = useState<BankBalanceEntry[]>([]);
    const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
    const [summary, setSummary] = useState<CurrencySummary[]>([]);
    const [comparison, setComparison] = useState<BankComparison[]>([]);
    const [syncCheck, setSyncCheck] = useState<SyncCheckResult | null>(null);
    const [resolvedCompany, setResolvedCompany] = useState<string | null>(null);

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [entriesResult, summaryResult, comparisonResult] = await Promise.allSettled([
                bankBalanceApi.listEntries(year, month, companyCode || undefined),
                bankBalanceApi.getSummary(year, month, companyCode || undefined),
                bankBalanceApi.getComparison(year, month, companyCode || undefined),
            ]);

            if (entriesResult.status === 'fulfilled') {
                setEntries(entriesResult.value.entries);
                setResolvedCompany(entriesResult.value.companyCode);

                // Fetch bank accounts for the resolved company
                const resolved = entriesResult.value.companyCode;
                const accounts = await bankBalanceApi.getBankAccounts(resolved);
                setBankAccounts(accounts);
            } else {
                setError(entriesResult.reason?.message || 'Failed to load entries');
            }

            if (summaryResult.status === 'fulfilled') setSummary(summaryResult.value);
            if (comparisonResult.status === 'fulfilled') setComparison(comparisonResult.value);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
        } finally {
            setLoading(false);
        }
    }, [year, month, companyCode]);

    const fetchSyncCheck = useCallback(async () => {
        try {
            const result = await bankBalanceApi.syncCheck();
            setSyncCheck(result);
        } catch {
            // Non-critical, don't show error
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchSyncCheck();
    }, [fetchSyncCheck]);

    const createEntry = useCallback(async (data: CreateEntryData) => {
        try {
            await bankBalanceApi.createEntry(data);
            toast('Entry created', 'success');
            await fetchData();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to create entry', 'error');
            throw err;
        }
    }, [fetchData, toast]);

    const updateEntry = useCallback(async (id: number, data: UpdateEntryData) => {
        try {
            await bankBalanceApi.updateEntry(id, data);
            toast('Entry updated', 'success');
            await fetchData();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update entry', 'error');
            throw err;
        }
    }, [fetchData, toast]);

    const deleteEntry = useCallback(async (id: number) => {
        try {
            await bankBalanceApi.deleteEntry(id);
            toast('Entry deleted', 'success');
            await fetchData();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to delete entry', 'error');
            throw err;
        }
    }, [fetchData, toast]);

    const copyEntries = useCallback(async (data: CopyEntriesData) => {
        try {
            const result = await bankBalanceApi.copyEntries(data);
            // Accounts that already have a balance on the target date are skipped,
            // never duplicated - say so, otherwise a partial copy looks like a full one.
            toast(
                result.skippedCount > 0
                    ? `Copied ${result.copiedCount} entries, skipped ${result.skippedCount} that already had a balance on that date`
                    : `Copied ${result.copiedCount} entries`,
                result.skippedCount > 0 ? 'info' : 'success'
            );
            await fetchData();
            return result;
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to copy entries', 'error');
            throw err;
        }
    }, [fetchData, toast]);

    return {
        // Filters
        year, setYear,
        month, setMonth,
        companyCode, setCompanyCode,
        resolvedCompany,

        // Data
        entries,
        bankAccounts,
        summary,
        comparison,
        syncCheck,

        // State
        loading,
        error,

        // Actions
        createEntry,
        updateEntry,
        deleteEntry,
        copyEntries,
        refresh: fetchData,
        refreshSyncCheck: fetchSyncCheck,
    };
}
